import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCall } from '../../services/api';

import DropDownFilter from '../../components/DropDownFilter';
import Note from './components/Note';
import Input from './components/Input';

import { ModalFilter, FilterOption as FO, NotesFormat, Status as StatusFormat } from '../../types';
import { breakpoints } from '../../base/variables.styled';

import { ReactComponent as NoteSubmitIcon } from '../../assets/icons/note-submit-icon.svg';
import { ReactComponent as NoteSubmitIconDisabled } from '../../assets/icons/note-submit-icon-disabled.svg';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/arrow-icon-white.svg';
import { ReactComponent as ExitIcon } from '../../assets/icons/exit-icon.svg';
import { ReduxStates } from '../../redux/reduxTypes';
import { formatDateFilter } from '../../helpers/format';
import { deleteNote, fetchNotes, postNote } from './helpers';
import Status from '../../components/Status';
import moment from 'moment';
import BaseButton from '../../components/BaseButton';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/chevron-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/chevron-left.svg';
import NotesFilters from './components/notesFilters';
import { setDatesFilters } from '../../redux/actions/filters';
import { useHistory } from 'react-router-dom';
import { setUnreadNotes } from '../../redux/actions/data';
import { useOktaAuth } from '@okta/okta-react';

const Notes = (): React.ReactElement => {
  const filters = useSelector((state: ReduxStates) => state.filters);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const config = useSelector((state: ReduxStates) => state.config);
  const data = useSelector((state: ReduxStates) => state.data);
  const user = useSelector((state: ReduxStates) => state.user);

  const [dateFilter, setDateFilter] = useState<string>('This Month');
  const [customRangeFilter, setCustomRangeFilter] = useState<Array<number | string> | null>(filters.dates?.customNotes);

  const [containerHeight, setContainerHeight] = useState<number>(window.innerHeight);
  const [selectedMarketTags, setSelectedMarketTags] = useState<Array<number> | null>([]);
  const [selectedMetricTags, setSelectedMetricTags] = useState<Array<number> | null>([]);
  const [selectedRetailerTags, setSelectedRetailerTags] = useState<Array<number> | null>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [notes, setNotes] = useState<Array<NotesFormat>>([]);

  const [marketFilters, setMarketFilters] = useState<ModalFilter | null>(null);
  const [retailerFilters, setRetailerFilters] = useState<ModalFilter | null>(null);
  const [metricFilters, setMetricFilters] = useState<ModalFilter | null>(null);
  const [metricTagFilters, setMetricTagFilters] = useState<ModalFilter | null>(null);

  const [selectedMetric, setSelectedMetric] = useState<number[] | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<number[] | null>(null);
  const [selectedRetailer, setSelectedRetailer] = useState<number[] | null>(null);
  const [status, setStatus] = useState<StatusFormat | null>(null);
  const [fetching, setFetching] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const records = [20, 50, 100];
  const [recordsPerPage, setRecordsPerPage] = useState<number>(records[0]);
  const [noteToDelete, setNoteToDelete] = useState<number>(-1);
  const dispatch = useDispatch();
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();
  const [hasWriteAccess, setHasWriteAccess] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [noteIdToEdit, setNoteIdToEdit] = useState<number | null>(null);
  const [tagsVisibility, setTagsVisibility] = useState<boolean>(false);
  const [fetchingMetricTags, setFetchingMetricTags] = useState<boolean>(false);
  const [fetchingMetrics, setFetchingMetrics] = useState<boolean>(false);
  const [isCommentPosted, setIsCommentPosted] = useState<boolean>(false);
  const [gridWidth, setGridWidth] = useState<number>(window.innerWidth);
  const [inputHeight, setInputHeight] = useState<number>(190);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user && user.userModules) {
      if (!user.userModules.includes('Notes: Read')) history.push('/glance');
      setHasWriteAccess(user.userModules.includes('Notes: Write'));
    }
  }, [user]);

  // set document title
  document.title = 'Notes - Ascential Connect';

  const inputEl: any = useRef(null);
  const threadEl: any = useRef(null);

  const returnContainerHeight = (): number => {
    const navigationHeight = 56; // statically set Navigation.styled.ts
    const filterHeight = 40; // statically set on ui.styled.ts

    return containerHeight - (navigationHeight + filterHeight) - 40;
  };

  const fetchFilters = async () => {
    // Fetch market and retailer filters
    try {
      setFetchingMetrics(true);
      setFetchingMetricTags(true);
      const fetchedMarketFilters: Array<FO> = await fetchCall(`${config.react_app_api_base_url}/filter/market`);
      const allMarketFilters = [
        {
          id: '0',
          name: 'All'
        },
        ...fetchedMarketFilters
      ];
      setMarketFilters({
        name: 'Market',
        values: allMarketFilters
      });

      const fetchedRetailerFilters: Array<FO> = await fetchCall(`${config.react_app_api_base_url}/filter/retailer`);
      const allRetailerFilters = [
        {
          id: '0',
          name: 'All'
        },
        ...fetchedRetailerFilters
      ];
      setRetailerFilters({
        name: 'Retailer',
        values: allRetailerFilters
      });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMetricFilter = async () => {
    // Fetch metric filter
    const params = {
      retailerIds: selectedRetailer?.toString()
    };
    try {
      const fetchedMetricFilters: Array<FO> = await fetchCall(`${config.react_app_api_base_url}/filter/metric`, params);
      const allMetricFilters = [
        {
          id: '0',
          name: 'All'
        },
        ...fetchedMetricFilters
      ];
      setMetricFilters({
        name: 'Metric',
        values: allMetricFilters
      });
      setFetchingMetrics(false);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMetricTagFilter = async () => {
    // Fetch metric tag filter
    const params = {
      retailerIds: selectedRetailerTags?.toString()
    };
    try {
      const fetchedMetricFilters: Array<FO> = await fetchCall(`${config.react_app_api_base_url}/filter/metric`, params);
      const allMetricFilters = [
        {
          id: '0',
          name: 'All'
        },
        ...fetchedMetricFilters
      ];
      setMetricTagFilters({
        name: 'Metric',
        values: allMetricFilters
      });
      setFetchingMetricTags(false);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Handles text area growth style handling
   * @param event
   */
  const handleTextGrowth = (event: any) => {
    const heightLimit = 125;

    if (inputEl && threadEl && event.target.scrollHeight <= heightLimit) {
      const inputElement: HTMLElement = inputEl.current;
      event.target.style.height = 'inherit';
      event.target.style.height = `${event.target.scrollHeight}px`;
      setInputHeight(inputElement.clientHeight + 60);
    }
  };

  const promptForDeletion = async (noteId: number) => {
    setNoteToDelete(noteId);
    setIsDeleting(true);
  };

  const handleNoteDeletion = async () => {
    setNoteToDelete(noteToDelete);
    deleteNote(noteToDelete)
      .then(() => {
        setNotes([]);
        fetchNewNotes();
        setStatus({
          state: 'success',
          message: `Note has been deleted successfully!`
        });
      })
      .catch((error) => {
        console.error(error);
        setStatus({
          state: 'error',
          message: `Something went wrong Deleting note with ID ${noteToDelete}. Please try again.`
        });
      });
    setIsDeleting(false);
  };

  const resetInputValues = () => {
    // reset input values
    setInputValue('');
    setSelectedMarketTags([]);
    setSelectedMetricTags([]);
    setSelectedRetailerTags([]);
    setTagsVisibility(false);
    const inputElement: HTMLElement = inputEl.current;
    // @ts-ignore - resets original textarea/text container height
    inputElement.childNodes[0].style.height = '50px';
    setInputHeight(190);
  };

  const handleNoteEditing = (note: NotesFormat) => {
    setIsEditing(true);
    const selectedMarketsID = note.markets?.split(',').map((market) => {
      return Number(marketFilters?.values.filter((value) => value.name.toLowerCase() === market.toLowerCase())[0]?.id);
    });
    const selectedMetricsID = note.metrics?.split(',').map((metric) => {
      return Number(
        metricTagFilters?.values.filter((value) => value.name.toLowerCase() === metric.toLowerCase())[0]?.id
      );
    });
    const selectedRetailersID = note.retailers?.split(',').map((retailer) => {
      return Number(
        retailerFilters?.values.filter((value) => value.name.toLowerCase() === retailer.toLowerCase())[0].id
      );
    });

    selectedMarketsID && setSelectedMarketTags(selectedMarketsID);
    selectedMetricsID && setSelectedMetricTags(selectedMetricsID);
    selectedRetailersID && setSelectedRetailerTags(selectedRetailersID);
    setInputValue(note.message);
    setNoteIdToEdit(note.noteId);
  };

  useEffect(() => {
    if (!isEditing && inputValue.length > 0) {
      setInputValue('');
    }
  }, [isEditing]);

  const handleEditSubmission = () => {
    if (!inputValue || inputValue.trim().length === 0) {
      setStatus({
        state: 'error',
        message: 'Note should not be empty. Please try again.'
      });
      return;
    }
    if (selectedMarketTags === null || selectedMetricTags === null || selectedRetailerTags === null) {
      setStatus({
        state: 'error',
        message: 'Tags are required. Please try again.'
      });
      return;
    }
    if (inputValue.length > 500) {
      setStatus({
        state: 'error',
        message: 'Note should not be more than 500 characters long.'
      });
      return;
    }
    if (inputEl && noteIdToEdit && !fetchingMetricTags) {
      postNote({
        noteId: noteIdToEdit,
        message: inputValue,
        marketIds: selectedMarketTags,
        metricIds: selectedMetricTags,
        retailerIds: selectedRetailerTags
      })
        .then(() => {
          setNotes([]);
          fetchNewNotes();
          setTagsVisibility(true);
          setStatus({
            state: 'success',
            message: `Note has been edited successfully!`
          });
        })
        .catch((error) => {
          console.error(error);
          setStatus({
            state: 'error',
            message: `Something went wrong while editing note. Please try again.`
          });
        });
      resetInputValues();
      setIsEditing(false);
    }
  };

  const fetchNewNotes = async () => {
    setFetching(true);
    const dateFilters = formatDateFilter(dateFilter, customRangeFilter || undefined, copyData);
    const formattedDates = dateFilters.split(' - ');
    const startDate = moment(new Date(formattedDates[0])).format('YYYY-MM-DD');
    const endDate = moment(new Date(formattedDates[1])).format('YYYY-MM-DD');
    const params = {
      startDate,
      endDate,
      pageNumber: currentPage + 1,
      recordPerPage: recordsPerPage,
      marketIds: selectedMarket?.toString(),
      metricIds: selectedMetric?.toString(),
      retailerIds: selectedRetailer?.toString()
    };
    fetchNotes(params, oktaAuth)
      .then((fetchedNotes) => {
        setNotes(fetchedNotes.notes);
        setTotalPages(fetchedNotes.pagingInfo?.totalPages);
        setFetching(false);
        isCommentPosted && setIsCommentPosted(false);
        if (data.newMsgsCount > 0) {
          dispatch(setUnreadNotes(0));
        }
      })
      .catch((error) => {
        console.error(error);
        setStatus({
          state: 'error',
          message: `Something went wrong fetching notes. Please try again.`
        });
        setFetching(false);
      });
  };

  const handleSubmit = async () => {
    if (!inputValue || inputValue.trim().length === 0) {
      setStatus({
        state: 'error',
        message: 'Note should not be empty. Please try again.'
      });
      return;
    }
    if (selectedMarketTags === null || selectedMetricTags === null || selectedRetailerTags === null) {
      setStatus({
        state: 'error',
        message: 'Tags are required. Please try again.'
      });
      return;
    }
    if (inputValue.length > 500) {
      setStatus({
        state: 'error',
        message: 'Note should not be more than 500 characters long.'
      });
      return;
    }
    if (inputEl) {
      postNote({
        message: inputValue,
        marketIds: selectedMarketTags,
        metricIds: selectedMetricTags,
        retailerIds: selectedRetailerTags
      })
        .then(() => {
          setCurrentPage(0);
          setIsCommentPosted(true);
          setTagsVisibility(true);
          setStatus({
            state: 'success',
            message: `Note has been posted successfully!`
          });
        })
        .catch((error) => {
          console.error(error);
          setStatus({
            state: 'error',
            message: `Something went wrong while posting note. Please try again.`
          });
        });

      resetInputValues();
    }
  };

  // Handles
  useEffect(() => {
    const resizeHandler = () => {
      if (window.innerHeight < breakpoints.height) {
        return setContainerHeight(breakpoints.height);
      }
      setContainerHeight(window.innerHeight);
    };
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [window.innerHeight]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    setFetchingMetrics(true);
    fetchMetricFilter();
  }, [selectedRetailer]);

  useEffect(() => {
    setFetchingMetricTags(true);
    fetchMetricTagFilter();
  }, [selectedRetailerTags]);

  // Scroll to bottom of thread on notes reload
  useEffect(() => {
    if (threadEl) {
      const threadElement: HTMLElement = threadEl.current;
      threadElement.scrollTo(0, threadElement.scrollHeight);
    }
  }, [notes]);

  useEffect(() => {
    if (
      data.dateFilterRangeLimit &&
      copyData &&
      recordsPerPage &&
      selectedMarket &&
      selectedMetric &&
      selectedRetailer
    ) {
      setNotes([]);
      fetchNewNotes();
    }
  }, [
    recordsPerPage,
    currentPage,
    dateFilter,
    customRangeFilter,
    data.dateFilterRangeLimit,
    selectedMarket,
    selectedMetric,
    copyData
  ]);

  useEffect(() => {
    if (isCommentPosted && currentPage === 0) {
      setNotes([]);
      fetchNewNotes();
    }
  }, [isCommentPosted]);

  useEffect(() => {
    if (status !== null) {
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  }, [status]);

  useEffect(() => {
    if (gridWidth < 1070) {
      setInputHeight(250);
    } else {
      setInputHeight(190);
    }
  }, [gridWidth]);

  useEffect(() => {
    const handleResize = () => setGridWidth(document.getElementsByClassName('notes-grid')[0]?.clientWidth);
    window.addEventListener('resize', handleResize);
  });

  const buttonStyles = `text-sm font-nunitoBold cursor-pointer mx-1 h-[50px] border-none bg-white border border-[#b9b9b9] rounded-[3px] px-2 ${
    isDropdownOpen ? 'last:bg-[#b9b9b9]' : 'last:bg-[#009965]'
  } hover:first:bg-[#b9b9b9] hover:first:text-white last:text-white last:border-[#009965]`;

  return (
    <div className="notes-grid pt-[30px] px-6 relative">
      {data.newMsgsCount > 0 && (
        <div className="flex flex-col items-center w-full">
          {' '}
          <button
            onClick={() => {
              setNotes([]);
              fetchNewNotes();
            }}
            className="inline-flex items-center content-center font-nunitoMedium text-sm rounded-[24px] absolute bg-navy text-white z-50 mt-[50px] py-1 px-3"
          >
            <DownArrowIcon className="pr-1" />
            {data.newMsgsCount} new messages
          </button>
        </div>
      )}
      {status && <Status state={status.state} message={status.message} onExit={() => setStatus(null)} />}
      {isDeleting && (
        <div className="fixed h-screen w-screen top-0 left-0 bg-[rgba(0,0,0,0.6)] z-50 flex items-center justify-center">
          <div className="w-full max-w-[586px] py-[34px] px-8 rounded-lg relative bg-white">
            <ExitIcon
              className="absolute top-[34px] right-[34px] cursor-pointer"
              onClick={() => setIsDeleting(false)}
            />
            <h3 className="m-0 font-nunitoBold">Delete Message</h3>

            <p className="m-0 mt-[15px] font-nunitoRegular text-sm tracking-[0.15px] leading-[21.82px]">
              Are you sure you want to delete this message? <br /> This cannot be undone.
            </p>
            <div className="w-full flex justify-end mt-6">
              <button
                className="cursor-pointer mr-2 hover:border-[#aeadad] hover:bg-[#aeadad] hover:text-white rounded-[3px] font-nunitoBold text-sm tracking-[0.4px] py-[8.5px] px-4"
                onClick={() => setIsDeleting(false)}
              >
                Cancel
              </button>
              <button
                className="cursor-pointer rounded-[3px] font-nunitoBold text-sm tracking-[0.4px] py-[8.5px] px-4 border-[#d43700] bg-[#d43700] hover:bg-[#b93505] text-white"
                onClick={() => handleNoteDeletion()}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={` ${gridWidth > 1170 ? 'flex h-[40px]' : 'flex-row h-[100px]'} justify-between w-full `}>
        <NotesFilters
          marketFilters={marketFilters}
          dropup={false}
          metricFilters={metricFilters}
          retailerFilters={retailerFilters}
          setSelectedMarkets={setSelectedMarket}
          setSelectedMetrics={setSelectedMetric}
          setSelectedRetailer={setSelectedRetailer}
          setCurrentPage={setCurrentPage}
          fetchingMetrics={fetchingMetrics}
          gridWidth={gridWidth}
          isTopFilter={true}
        />

        <div className="flex items-center">
          <DropDownFilter
            variant="globalDateFilter"
            isDateFilter
            value={formatDateFilter(dateFilter, customRangeFilter || undefined, copyData)}
            onChange={(option: string, customRange: Array<number | string> | undefined) => {
              setDateFilter(option);
              setDatesFilters;
              dispatch(setDatesFilters({ range: option }));
              if (customRange) {
                setCustomRangeFilter(customRange);
                dispatch(setDatesFilters({ range: option, customNotes: customRange }));
              } else {
                setCustomRangeFilter(null);
              }
            }}
            automationID="global_date_picker"
            isNotesScreen={true}
          />
        </div>
      </div>
      <div className="relative bg-white rounded mt-2.5" style={{ height: returnContainerHeight() }}>
        <div
          className={`box-border px-8 ${hasWriteAccess ? 'min-h-[420px]' : 'min-h-[550px]'}  overflow-y-scroll`}
          ref={threadEl}
          style={{ maxHeight: hasWriteAccess ? returnContainerHeight() - inputHeight : returnContainerHeight() - 50 }}
        >
          <h3 className="font-nunitoRegular text-base text-center py-5">
            {data.dateFilterRangeLimit ? formatDateFilter(dateFilter, customRangeFilter || undefined, copyData) : ''}
          </h3>
          {notes?.length ? (
            notes.map((note) => (
              <Note
                note={note}
                onDelete={(noteId) => promptForDeletion(noteId)}
                onEdit={(value) => handleNoteEditing(value)}
                setIsEditing={setIsEditing}
                hasWriteAccess={hasWriteAccess}
              />
            ))
          ) : fetching ? (
            <p className="font-nunitoBold text-filterList text-base text-center mt-[150px]">Loading...</p>
          ) : (
            <p className="font-nunitoBold text-filterList text-base text-center mt-[150px]">
              No comments exist for the chosen filters.
            </p>
          )}

          {/* NULL UI */}
          {/* <NullContainer>
            <Null/>
            <h3>No notes for this month</h3>
            <p>Post a message below to start</p>
          </NullContainer> */}
        </div>

        <div className="absolute bottom-0 w-full bg-white rounded-b shadow-notesInputShadow">
          <div className="flex items-center w-full px-[30%]">
            {totalPages > 0 && (
              <React.Fragment>
                <span className="text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 inline uppercase">
                  {'Page Size:'}
                </span>
                <DropDownFilter
                  options={records}
                  onChange={(option) => {
                    setCurrentPage(0);
                    setRecordsPerPage(parseInt(option));
                  }}
                  value={recordsPerPage}
                  variant={'minimal'}
                  automationID={'records_per_page_picker'}
                />
                <BaseButton
                  variant={currentPage > 0 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage > 0) setCurrentPage(0);
                  }}
                >
                  <span>{copyData?.data_mapping_grid_first}</span>
                </BaseButton>
                <BaseButton
                  variant={currentPage > 0 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage > 0) setCurrentPage(currentPage - 1);
                  }}
                >
                  <span>
                    <ArrowLeftIcon />
                  </span>
                </BaseButton>
                <div>
                  <p className="font-nunitoLight text-sm">
                    {copyData?.data_mapping_grid_page}: {currentPage + 1} {copyData?.data_mapping_grid_of} {totalPages}
                  </p>
                </div>
                <BaseButton
                  variant={currentPage < totalPages - 1 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
                  }}
                >
                  <span>
                    <ArrowRightIcon />
                  </span>
                </BaseButton>
                <BaseButton
                  variant={currentPage < totalPages - 1 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage < totalPages - 1) setCurrentPage(totalPages - 1);
                  }}
                >
                  <span>{copyData?.data_mapping_grid_last}</span>
                </BaseButton>
              </React.Fragment>
            )}
          </div>
          {hasWriteAccess ? (
            <div className="flex justify-between w-full box-border py-[10px] px-8">
              <Input
                requireTags
                handleTextGrowth={handleTextGrowth}
                inputEl={inputEl}
                values={{
                  input: inputValue,
                  marketIds: selectedMarketTags,
                  metricIds: selectedMetricTags,
                  retailerIds: selectedRetailerTags
                }}
                onChange={(values) => {
                  setSelectedMarketTags(values.marketIds);
                  setSelectedMetricTags(values.metricIds);
                  setSelectedRetailerTags(values.retailerIds);
                  setInputValue(values.input);
                }}
                marketFilters={marketFilters ? marketFilters : null}
                metricFilters={metricTagFilters ? metricTagFilters : null}
                retailerFilters={retailerFilters ? retailerFilters : null}
                tagsVisibility={tagsVisibility}
                isEditing={isEditing}
                fetchingMetrics={fetchingMetricTags}
                gridWidth={gridWidth}
                setIsDropdownOpen={setIsDropdownOpen}
              />
              {isEditing ? (
                <div className="flex">
                  <button
                    className={buttonStyles}
                    onClick={() => {
                      setIsEditing(false);
                      resetInputValues();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className={buttonStyles}
                    disabled={isDropdownOpen && !fetchingMetrics}
                    onClick={handleEditSubmission}
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <button
                  className={`text-sm font-nunitoBold h-[50px] ml-3 rounded-[50px] border-none bg-white`}
                  disabled={isDropdownOpen && !fetchingMetrics}
                  onClick={handleSubmit}
                >
                  {!isDropdownOpen ? <NoteSubmitIcon /> : <NoteSubmitIconDisabled className="pointer-events-none" />}
                </button>
              )}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};

export default Notes;
